"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useDomainSearch = void 0;
var _react = require("react");
var _domainServices = require("@site/domain-services");
var _patternLibraryUtils = require("@site/pattern-library-utils");
var _searchContext = require("./search-context");
function getISC() {
  // copies behavior from serp-app repo
  const urlQueryParams = new URLSearchParams(window?.location?.search);
  return urlQueryParams.get('isc') || (0, _patternLibraryUtils.getCookieValue)('isc') || (0, _patternLibraryUtils.getCookieValue)('aff_isc') || '';
}
const getMarket = () => {
  return (0, _patternLibraryUtils.getCookieValue)('market') || 'en-US';
};

/**
 * Context-aware useDomainSearch hook
 * Custom hook for fetching domain search results with DomainServices.
 * Gets search query and SERP configuration from the internal search context.
 *
 * @param {Object} domainServicesProps - Additional props for DomainServices
 * @returns {Object} Search Data with isLoading and searchData
 */
const useDomainSearch = (domainServicesProps = {}) => {
  const {
    searchValue: searchQuery,
    currentDomainSearch: serp
  } = (0, _searchContext.useInternalSearch)();
  const {
    apiKey,
    plid,
    env = (0, _patternLibraryUtils.getDefaultEnv)(),
    market = getMarket(),
    itc,
    maxSpins
  } = serp || {};
  const [searchData, setSearchData] = (0, _react.useState)({
    exactResults: null,
    spinResults: null,
    error: null,
    hasSpins: false
  });
  const [isLoading, setIsLoading] = (0, _react.useState)(false);
  (0, _react.useEffect)(() => {
    /* istanbul ignore next */
    if (typeof window === 'undefined') {
      return;
    }
    if (searchData[searchQuery]) {
      // If the search data already exists, don't make another fetch call
      return;
    }
    setIsLoading(true);
    const makeFetchCall = async () => {
      try {
        const domainServices = new _domainServices.DomainServices({
          fetchEntity: window.fetch,
          apiKey: apiKey,
          plid: plid,
          env: env,
          market: market,
          ...domainServicesProps
        });
        const extraParams = {
          partial_query: searchQuery,
          extravalue: 'important',
          pagesize: maxSpins || 5,
          isc: getISC(),
          itc: itc
        };
        const findExactResults = await domainServices.findExact({
          query: searchQuery,
          extraParams
        });
        const findSpinResults = await domainServices.findSpins({
          query: searchQuery,
          extraParams
        });
        setSearchData(prevState => ({
          ...prevState,
          [searchQuery]: {
            exactResults: findExactResults,
            spinResults: findSpinResults,
            hasSpins: findSpinResults?.Products != null,
            error: null
          }
        }));
      } catch (err) {
        setSearchData(prevState => ({
          ...prevState,
          [searchQuery]: {
            ...prevState[searchQuery],
            error: err.message
          }
        }));
      } finally {
        setIsLoading(false);
      }
    };
    if (searchQuery) {
      makeFetchCall();
    }
  }, [searchData, searchQuery, apiKey, plid, env, market, itc, domainServicesProps]);
  return {
    searchData,
    isLoading
  };
};
exports.useDomainSearch = useDomainSearch;